import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, FolderOpen, Package, Table as TableIcon, ClipboardList, Home, History, User, LogOut } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  screen: string;
}

const BottomNavigation: React.FC = () => {
  const { currentScreen, setCurrentScreen } = useApp();
  const { user } = useAuth();
  const [customerSession, setCustomerSession] = useState<any>(null);

  useEffect(() => {
    // Check for customer session
    const session = localStorage.getItem('bookfood_customer_session');
    if (session) {
      try {
        setCustomerSession(JSON.parse(session));
      } catch (error) {
        console.error('Error parsing customer session:', error);
        setCustomerSession(null);
      }
    } else {
      setCustomerSession(null);
    }
  }, [currentScreen]); // Re-check when screen changes

  const adminNavItems: NavigationItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={22} />, screen: 'admin-dashboard' },
    { id: 'category', label: 'Category', icon: <FolderOpen size={22} />, screen: 'admin-category' },
    { id: 'product', label: 'Product', icon: <Package size={22} />, screen: 'admin-product' },
    { id: 'table', label: 'Table', icon: <TableIcon size={22} />, screen: 'admin-table' },
    { id: 'orders', label: 'Orders', icon: <ClipboardList size={22} />, screen: 'admin-orders' }
  ];

  const customerNavItems: NavigationItem[] = [
    { id: 'home', label: 'Home', icon: <Home size={22} />, screen: 'customer-home' },
    { id: 'history', label: 'History', icon: <History size={22} />, screen: 'customer-history' },
    { id: 'paybill', label: 'Pay Bill', icon: <ClipboardList size={22} />, screen: 'customer-paybill' },
    { id: 'profile', label: 'Profile', icon: <User size={22} />, screen: 'customer-profile' }
  ];

  const handleCustomerLogout = () => {
    localStorage.removeItem('bookfood_customer_session');
    localStorage.removeItem('bookfood_selectedTableId');
    setCurrentScreen('welcome');
  };

  // Determine navigation based on customer session or user role
  const isCustomer = customerSession || (user && user.role === 'customer');
  const navItems = isCustomer ? customerNavItems : adminNavItems;

  const handleNavigation = (screen: string) => {
    setCurrentScreen(screen as any);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-lg bg-slate-900 bg-opacity-90 border-t border-gray-800 shadow-2xl">
      <div className="max-w-7xl mx-auto px-2">
        <div className="flex justify-around py-3">
          {navItems.map((item) => {
            const isActive = currentScreen === item.screen;
            
            return (
              <motion.button
                key={item.id}
                onClick={() => handleNavigation(item.screen)}
                className={`flex flex-col items-center py-2 px-4 rounded-xl transition-all relative ${
                  isActive 
                    ? 'text-orange-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={item.label}
              >
                <div className={`mb-1 ${isActive ? 'transform scale-110' : ''}`}>
                  {item.icon}
                </div>
                <span className={`text-xs font-semibold ${isActive ? 'font-bold' : ''}`}>
                  {item.label}
                </span>
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                  />
                )}
              </motion.button>
            );
          })}
          
          {/* Customer Logout Button */}
          {customerSession && (
            <motion.button
              onClick={handleCustomerLogout}
              className="flex flex-col items-center py-2 px-4 rounded-xl transition-all text-gray-400 hover:text-red-400"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Logout"
            >
              <div className="mb-1">
                <LogOut size={22} />
              </div>
              <span className="text-xs font-semibold">
                Logout
              </span>
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BottomNavigation;
